import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  getVideos,
  getVideo,
  updateVideo,
  deleteVideo,
  repickVideoPoster,
  restoreVideoPoster,
  uploadVideo,
  getVideoGroups,
  getVideoGroup,
  createVideoGroup,
  updateVideoGroup,
  deleteVideoGroup,
  addVideoToGroup,
  removeVideoFromGroup,
  reorderVideoGroup,
  getVideoGroupCoverToken,
  uploadVideoGroupCover,
  type GetVideosParams,
  type UploadVideoParams,
} from '../api/videos';

export const videoKeys = {
  all: ['videos'] as const,
  lists: () => [...videoKeys.all, 'list'] as const,
  list: (params?: GetVideosParams) => [...videoKeys.lists(), params] as const,
  details: () => [...videoKeys.all, 'detail'] as const,
  detail: (id: number) => [...videoKeys.details(), id] as const,
};

export function useVideos(params?: GetVideosParams) {
  return useQuery({
    queryKey: videoKeys.list(params),
    queryFn: () => getVideos(params),
  });
}

export function useVideo(id: number) {
  return useQuery({
    queryKey: videoKeys.detail(id),
    queryFn: () => getVideo(id),
    enabled: id > 0,
  });
}

export function useUpdateVideo() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      id,
      params,
    }: {
      id: number;
      params: { title?: string; description?: string };
    }) => updateVideo(id, params),
    onSuccess: (updated) => {
      // The PATCH returns the fresh row; prime the detail cache so the UI
      // reflects the edit without a refetch, then invalidate lists/groups
      // whose titles are now stale.
      queryClient.setQueryData(videoKeys.detail(updated.id), updated);
      queryClient.invalidateQueries({ queryKey: videoKeys.lists() });
      queryClient.invalidateQueries({ queryKey: groupKeys.all });
    },
  });
}

export function useRepickVideoPoster() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, atSecs }: { id: number; atSecs: number }) => repickVideoPoster(id, atSecs),
    onSuccess: (updated) => {
      queryClient.setQueryData(videoKeys.detail(updated.id), updated);
      queryClient.invalidateQueries({ queryKey: videoKeys.lists() });
      queryClient.invalidateQueries({ queryKey: groupKeys.all });
    },
  });
}

export function useRestoreVideoPoster() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: number) => restoreVideoPoster(id),
    onSuccess: (updated) => {
      queryClient.setQueryData(videoKeys.detail(updated.id), updated);
      queryClient.invalidateQueries({ queryKey: videoKeys.lists() });
      queryClient.invalidateQueries({ queryKey: groupKeys.all });
    },
  });
}

export function useDeleteVideo() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: number) => deleteVideo(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: videoKeys.all });
      queryClient.invalidateQueries({ queryKey: groupKeys.all });
    },
  });
}

export function useUploadVideo() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (params: UploadVideoParams) => uploadVideo(params),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: videoKeys.lists() });
    },
  });
}

export const groupKeys = {
  all: ['video-groups'] as const,
  lists: () => [...groupKeys.all, 'list'] as const,
  detail: (id: number) => [...groupKeys.all, 'detail', id] as const,
};

export function useVideoGroups() {
  return useQuery({
    queryKey: groupKeys.lists(),
    queryFn: getVideoGroups,
  });
}

export function useVideoGroup(id: number) {
  return useQuery({
    queryKey: groupKeys.detail(id),
    queryFn: () => getVideoGroup(id),
    enabled: id > 0,
  });
}

export function useCreateVideoGroup() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: createVideoGroup,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: groupKeys.lists() });
    },
  });
}

export function useUpdateVideoGroup() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      id,
      params,
    }: {
      id: number;
      params: { title?: string; description?: string; kind?: string };
    }) => updateVideoGroup(id, params),
    onSuccess: (_data, { id }) => {
      queryClient.invalidateQueries({ queryKey: groupKeys.detail(id) });
      queryClient.invalidateQueries({ queryKey: groupKeys.lists() });
    },
  });
}

export function useDeleteVideoGroup() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: deleteVideoGroup,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: groupKeys.lists() });
    },
  });
}

export function useAddVideoToGroup() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ groupId, videoId }: { groupId: number; videoId: number }) =>
      addVideoToGroup(groupId, videoId),
    onSuccess: (_data, { groupId }) => {
      queryClient.invalidateQueries({ queryKey: groupKeys.detail(groupId) });
      queryClient.invalidateQueries({ queryKey: groupKeys.lists() });
      queryClient.invalidateQueries({ queryKey: videoKeys.lists() });
    },
  });
}

export function useRemoveVideoFromGroup() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ groupId, videoId }: { groupId: number; videoId: number }) =>
      removeVideoFromGroup(groupId, videoId),
    onSuccess: (_data, { groupId }) => {
      queryClient.invalidateQueries({ queryKey: groupKeys.detail(groupId) });
      queryClient.invalidateQueries({ queryKey: groupKeys.lists() });
    },
  });
}

export function useReorderVideoGroup() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ groupId, videoIds }: { groupId: number; videoIds: number[] }) =>
      reorderVideoGroup(groupId, videoIds),
    // Refetch whether the reorder succeeded or failed, so the rendered order
    // always matches the server (a failed reorder must not leave a stale order
    // on screen).
    onSettled: (_data, _err, { groupId }) => {
      queryClient.invalidateQueries({ queryKey: groupKeys.detail(groupId) });
    },
  });
}

export const videoGroupCoverTokenKeys = {
  detail: (groupId: number) => ['video-group-cover-token', groupId] as const,
};

// Resolves an absolute, signed cover URL for a video group, only once
// `enabled` — mirrors useAudioGroupCoverUrl exactly (an <img> can't send an
// Authorization header, so the backend mints a short-lived ?t= token).
export function useVideoGroupCoverUrl(groupId: number, enabled: boolean): string | null {
  const { data } = useQuery({
    queryKey: videoGroupCoverTokenKeys.detail(groupId),
    queryFn: () => getVideoGroupCoverToken(groupId),
    enabled,
  });

  if (!data?.token) return null;
  return `/api/video-groups/${groupId}/cover?t=${data.token}`;
}

export function useUploadVideoGroupCover() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, file }: { id: number; file: File }) => uploadVideoGroupCover(id, file),
    onSuccess: (_data, { id }) => {
      queryClient.invalidateQueries({ queryKey: groupKeys.detail(id) });
      queryClient.invalidateQueries({ queryKey: groupKeys.lists() });
      queryClient.invalidateQueries({ queryKey: videoGroupCoverTokenKeys.detail(id) });
    },
  });
}
